
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRoutes } from './router';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import ToastHost from './components/base/ToastHost';
import { authService } from './services/authService';
import './index.css';

/**
 * A failed fetch must surface as an error, never as an empty/zero dataset —
 * silently rendering zeros as fact is what made the night audit report an
 * empty hotel. Retry transient failures, but never retry a 4xx.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number } | null)?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AuthExpiryRedirector = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleExpiry = () => {
      if (pathname !== '/login') {
        navigate('/login', { replace: true, state: { reason: 'session-expired' } });
      }
    };

    window.addEventListener('auth:expired', handleExpiry as EventListener);
    authService.validateSession();
    return () => window.removeEventListener('auth:expired', handleExpiry as EventListener);
  }, [navigate, pathname]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // Add cache-busting meta tags
    const addCacheBustingMeta = () => {
      // Remove existing cache control meta tags
      const existingMetas = document.querySelectorAll('meta[http-equiv="Cache-Control"], meta[http-equiv="Pragma"], meta[http-equiv="Expires"]');
      existingMetas.forEach(meta => meta.remove());

      // Add new cache control meta tags
      const cacheControlMeta = document.createElement('meta');
      cacheControlMeta.setAttribute('http-equiv', 'Cache-Control');
      cacheControlMeta.setAttribute('content', 'no-cache, no-store, must-revalidate');
      document.head.appendChild(cacheControlMeta);

      const pragmaMeta = document.createElement('meta');
      pragmaMeta.setAttribute('http-equiv', 'Pragma');
      pragmaMeta.setAttribute('content', 'no-cache');
      document.head.appendChild(pragmaMeta);

      const expiresMeta = document.createElement('meta');
      expiresMeta.setAttribute('http-equiv', 'Expires');
      expiresMeta.setAttribute('content', '0');
      document.head.appendChild(expiresMeta);
    };

    addCacheBustingMeta();

    // Clear service worker cache if exists
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }

    // Force reload if coming from login with fresh parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('fresh') === 'true') {
      console.log('Fresh load detected, ensuring latest version');
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
    <I18nextProvider i18n={i18n}>
      <ToastHost />
      <BrowserRouter basename={__BASE_PATH__}>
        <ScrollToTop />
        <AuthExpiryRedirector />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
        }>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </I18nextProvider>
    </QueryClientProvider>
  );
};

export default App;
